import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar } from '@/components/Calendar';
import { FlightCard } from '@/components/FlightCard';
import { UnitedQuickLinks } from '@/components/UnitedQuickLinks';
import { SavedFlightStatusCard } from '@/components/SavedFlightStatusCard';
import { SavedFlightEventRow } from '@/components/SavedFlightEventRow';
import { useFlightSchedule } from '@/hooks/useFlightSchedule';
import type { FlightDirection } from '@/types/api';
import { useSavedFlights } from '@/hooks/useSavedFlights';
import { formatDateLabel } from '@/lib/formatting';
import { markCalendarDay, savedFlightsOnDate } from '@/lib/flightCalendar';
import { schoolBreakDateRange, schoolBreaksOn } from '@/lib/schoolBreaks';
import { STAYS, stayDateRange, staySegmentOn, staysOn } from '@/lib/trips';

function shiftDate(dateKey: string, days: number): string {
  const d = new Date(`${dateKey}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function FlightsPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState<'find' | 'mine'>('find');
  const [selectedDate, setSelectedDate] = useState<string | null>(params.get('date'));
  const [travelers] = useState(1);

  // Direction survives a page reload via ?direction= so a shared link to a
  // specific search stays true to what was seen.
  const initialDirection: FlightDirection = params.get('direction') === 'inbound' ? 'inbound' : 'outbound';
  const [direction, setDirection] = useState<FlightDirection>(initialDirection);
  const { data, error, loading } = useFlightSchedule(selectedDate, travelers, direction);

  const routeLabel =
    direction === 'outbound'
      ? { sub: "Southwest Florida Int'l → Newark Liberty Int'l" }
      : { sub: "Newark Liberty Int'l → Southwest Florida Int'l" };
  const { flights: savedFlights, save, remove } = useSavedFlights();
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const savedIdents = useMemo(() => new Set(savedFlights.map((f) => `${f.ident}-${f.flight_date}`)), [savedFlights]);

  const savedByDate = useMemo(() => {
    const dates = new Set(savedFlights.map((f) => f.flight_date));
    return new Map([...dates].map((date) => [date, savedFlightsOnDate(savedFlights, date)]));
  }, [savedFlights]);

  const markDay = useCallback(
    (dateKey: string) => markCalendarDay(dateKey, savedByDate.get(dateKey)?.length ?? 0),
    [savedByDate],
  );

  const selectedMark = selectedDate ? markDay(selectedDate) : undefined;
  const selectedSaved = selectedDate ? savedByDate.get(selectedDate) ?? [] : [];
  const selectedBreaks = selectedDate ? schoolBreaksOn(selectedDate) : [];
  const selectedStays = selectedDate ? staysOn(selectedDate) : [];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Flights</h1>
        <p className="page-subtitle">{routeLabel.sub}</p>
      </div>

      <div
        role="tablist"
        aria-label="Flight direction"
        className="direction-toggle"
      >
        <button
          type="button"
          role="tab"
          aria-selected={direction === 'outbound'}
          className={`direction-toggle__btn ${direction === 'outbound' ? 'is-active' : ''}`}
          onClick={() => setDirection('outbound')}
        >
          RSW → EWR
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={direction === 'inbound'}
          className={`direction-toggle__btn ${direction === 'inbound' ? 'is-active' : ''}`}
          onClick={() => setDirection('inbound')}
        >
          EWR → RSW
        </button>
      </div>

      <div role="tablist" aria-label="Flights view" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'find'}
          className="btn"
          style={{ flex: 1, background: tab === 'find' ? 'var(--color-pine)' : 'var(--color-sand-dark)', color: tab === 'find' ? '#fff' : 'var(--color-ink)' }}
          onClick={() => setTab('find')}
        >
          Find a Flight
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'mine'}
          className="btn"
          style={{ flex: 1, background: tab === 'mine' ? 'var(--color-pine)' : 'var(--color-sand-dark)', color: tab === 'mine' ? '#fff' : 'var(--color-ink)' }}
          onClick={() => setTab('mine')}
        >
          My Flight{savedFlights.length > 0 ? ` (${savedFlights.length})` : ''}
        </button>
      </div>

      {tab === 'find' && (
        <>
          <div className="card">
            <Calendar selectedDate={selectedDate} onSelect={setSelectedDate} markDay={markDay} />
            <div className="calendar-legend">
              <span>
                <span className="calendar-legend__swatch" style={{ background: '#DCEFDD' }} />
                Not your parenting week — free to travel
              </span>
              <span>
                <span className="calendar-legend__swatch" style={{ background: '#E3F1F6' }} />
                Chip has kids
              </span>
              <span>
                <span className="calendar-legend__swatch" style={{ background: '#FBEAC4' }} />
                Federal holiday
              </span>
              <span>
                <span className="calendar-legend__swatch" style={{ background: 'var(--color-primary)' }} />
                Saved flight
              </span>
              <span>
                <span className="calendar-legend__swatch calendar-legend__swatch--barnstable" />
                Barnstable breaks & closures
              </span>
              <span>
                <span className="calendar-legend__swatch calendar-legend__swatch--skidmore" />
                Skidmore breaks & closures
              </span>
              {STAYS.flatMap((stay) =>
                stay.segments.map((seg) => (
                  <span key={`${stay.id}-${seg.start}`}>
                    <span className="calendar-legend__swatch" style={{ background: seg.color }} />
                    {seg.label}
                  </span>
                )),
              )}
            </div>
          </div>

          {selectedDate && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSelectedDate(shiftDate(selectedDate, -1))}>
                  ← Previous Day
                </button>
                <strong>{formatDateLabel(selectedDate)}</strong>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSelectedDate(shiftDate(selectedDate, 1))}>
                  Next Day →
                </button>
              </div>

              {selectedMark?.label && (
                <p style={{ fontSize: 13, color: 'var(--color-ink-muted)', textAlign: 'center', marginTop: -4, marginBottom: 8 }}>
                  {selectedMark.label}
                </p>
              )}

              {selectedBreaks.length > 0 && (
                <section className="saved-flight-day-list" aria-label="School breaks on this day">
                  <h2 className="saved-flight-day-list__title">School breaks</h2>
                  {selectedBreaks.map((b) => (
                    <article key={`${b.school}-${b.start}`} className="saved-flight-event" aria-label={`${b.school} ${b.name}`}>
                      <div className="saved-flight-event__ident">{b.school}</div>
                      <div className="saved-flight-event__route">{b.name}</div>
                      <div className="saved-flight-event__times">{schoolBreakDateRange(b)}</div>
                    </article>
                  ))}
                </section>
              )}

              {selectedStays.length > 0 && (
                <section className="saved-flight-day-list" aria-label={selectedStays.map((s) => s.name).join(', ')}>
                  {selectedStays.map((stay) => {
                    const seg = selectedDate ? staySegmentOn(stay, selectedDate) : undefined;
                    return (
                      <article key={stay.id} className="saved-flight-event" aria-label={stay.name}>
                        <div className="saved-flight-event__ident">{seg?.label ?? stay.name}</div>
                        {seg && seg.label !== stay.name && (
                          <div className="saved-flight-event__route">{stay.name}</div>
                        )}
                        <div className="saved-flight-event__times">{stayDateRange(stay)}</div>
                      </article>
                    );
                  })}
                </section>
              )}

              {selectedSaved.length > 0 && (
                <section className="saved-flight-day-list" aria-label="Saved flights on this day">
                  <h2 className="saved-flight-day-list__title">Saved flights</h2>
                  {selectedSaved.map((f) => (
                    <SavedFlightEventRow key={f.id} flight={f} />
                  ))}
                </section>
              )}

              {loading && <div className="skeleton" style={{ height: 140 }} />}

              {/* When the live schedule is unavailable, fall back to United
                  deep links rather than a dead error card. The original ask
                  was quick access to RSW<->EWR, and that needs no API key. */}
              {error && <UnitedQuickLinks date={selectedDate} />}

              {!loading && !error && data && data.itineraries.length === 0 && (
                <div className="card empty-state">
                  <p>No United flights found for {formatDateLabel(selectedDate)}.</p>
                </div>
              )}

              {!loading &&
                !error &&
                data?.itineraries.map((it) => {
                  const key = `${it.segments[0].ident}-${selectedDate}`;
                  return (
                    <FlightCard
                      key={it.key}
                      itinerary={it}
                      saved={savedIdents.has(key) || savingKey === key}
                      onSave={async () => {
                        setSavingKey(key);
                        await save(it.segments[0], selectedDate);
                        setSavingKey(null);
                      }}
                    />
                  );
                })}
            </>
          )}

          {!selectedDate && <p className="empty-state">Choose a departure date to see United flights.</p>}
        </>
      )}

      {tab === 'mine' && (
        <>
          {savedFlights.length === 0 && (
            <div className="card empty-state">
              <p>No saved flight yet. Find a flight and tap "Save This Flight".</p>
            </div>
          )}
          {savedFlights.map((f) => (
            <SavedFlightStatusCard key={f.id} saved={f} onRemove={() => remove(f.id)} />
          ))}
        </>
      )}
    </div>
  );
}
